import { useTranslation } from "react-i18next";
import {
  AddButton,
  ButtonsContainer,
  CancelButton,
} from "../addClientModal/addClientModal.styles";
import { useContext, useEffect, useState } from "react";
import { useParams, useSearchParams } from "react-router-dom";
import { Get, SendFormData, handleError } from "../../../utils/fetch.util";
import { UserContext } from "../../../contexts/user.context";
import { toast } from "react-toastify";
import { FilePond } from "react-filepond";
import i18next from "i18next";

type SiteModalProps = {
  isOpen: boolean;
  isLoading: boolean;
  setIsLoading: any;
  onClose: () => void;
  toggleModal: () => void;
  getSectors: () => void;
};

interface Country {
  id: number;
  name_en: string;
  name_ar: string;
  code_2: string;
  code_3: string;
  phone_code: string;
  enable: boolean;
  createdAt: string;
  updatedAt: string;
}

type Site = {
  asdc_client_sector_id: any;
  site_name: string;
  logo_url: string | undefined;
  site_url?: string;
  site_serial?: string;
  country_id?: number;
};

export default function AddSiteModal({
  isOpen,
  onClose,
  isLoading,
  setIsLoading,
  getSectors,
}: SiteModalProps) {
  const { t } = useTranslation();
  const { getValidToken, getProfile } = useContext(UserContext);
  const { sectorId } = useParams();
  const [site, setSite] = useState<Site>({
    asdc_client_sector_id: Number(sectorId),
    site_name: "",
    logo_url: "",
    site_url: "",
    site_serial: "",
    country_id: undefined,
  });
  const [logoUrl, setLogoUrl] = useState<any[]>([]);
  const [resetFileState, setResetFileState] = useState<boolean>(false);
  const [countries, setCountries] = useState<Country[]>([]);
  const [isLoadingCountries, setIsLoadingCountries] = useState<boolean>(false);
  const [searchParams] = useSearchParams();
  const clientId = searchParams.get("client_id");
  function containsCharacters(inputString: string): boolean {
    return /[a-zA-Z\u0600-\u06FF]/.test(inputString);
  }

  const getEnabledCountries = async () => {
    setIsLoadingCountries(true);
    try {
      const res = await Get(
        "country/get-all-countries?enable=true&pageItemsCount=1000",
        getValidToken()
      );
      
      if (res.error || !res.data) {
        handleError(res);
        return;
      }
      
      setCountries(res.data);
    } catch (error) {
      console.error("Error fetching countries:", error);
    } finally {
      setIsLoadingCountries(false);
    }
  };

  const addSite = async (e: any) => {
    e.preventDefault();
    setIsLoading(true);
    const formData = new FormData();
    const site_name_has_characters = containsCharacters(
      site.site_name as string,
    );
    if (!site_name_has_characters) {
      setIsLoading(false);
      return toast.error("Site name can't include numbers only");
    }

    formData.append("site_name", site.site_name);
    formData.append("site_url", site.site_url || "");
    formData.append("site_serial", site.site_serial || "");
    formData.append("asdc_client_sector_id", String(sectorId));
    if (clientId) {
      formData.append("asdc_client_id", String(clientId));
    }
    if (site.country_id) {
      formData.append("country_id", String(site.country_id));
    }

    const logoUrlModified =
      logoUrl?.length > 0 && logoUrl[0]?.file !== undefined;

    if (logoUrlModified) {
      logoUrl.forEach((item) => formData.append("logo_url", item.file));
    }

    const response = await SendFormData(
      "sites/add-site",
      formData,
      getValidToken(),
    );
    if (response.error || !response.data) {
      handleError(response);
      setIsLoading(false);
      return;
    }

    setIsLoading(false);
    setSite({
      asdc_client_sector_id: Number(sectorId),
      site_name: "",
      logo_url: "",
      site_serial: "",
      country_id: undefined,
    });
    setLogoUrl([]);
    // const newSector = sectors?.map((sector: Sector) => {
    //   if (sector.sites && (+sector.asdc_client_sector_id === Number(sectorId))) {
    //     const newSites = [...sector.sites, response.data];
    //     return { ...sector, sites: newSites };
    //   } else {
    //     return sector;
    //   }
    // });
    // setSectors(newSector);
    getProfile();
    setResetFileState(!resetFileState);
    onClose();
    getSectors();
  };

  useEffect(() => {
    if (isOpen) {
      getEnabledCountries();
    }
  }, [isOpen]);

  return (
    <>
      <form
        onSubmit={addSite}
        className={`absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2 bg-white w-[500px] p-6 !z-[999999] ${isOpen ? "opacity-100" : "opacity-0 pointer-events-none"
          }`}
      >
        <h2 className="text-3xl mb-6 !text-black">{t("structure_add_site")}</h2>
        <div className="flex flex-col gap-2 mt-5">
          <label aria-required>{t("structure_site_title")}</label>
          <input
            onChange={(e) => setSite({ ...site, site_name: e.target.value })}
            value={site.site_name}
            className="border"
            type="text"
            required
          />
        </div>
        <div className="flex flex-col gap-2 mt-5">
          <label>{t("structure_site_serial")}</label>
          <input
            onChange={(e) => setSite({ ...site, site_serial: e.target.value })}
            value={site.site_serial}
            className="border"
            type="text"
          />
        </div>
        <div className="flex flex-col gap-2 mt-5">
          <label>{t("structure_site_url")}</label>
          <input
            onChange={(e) => setSite({ ...site, site_url: e.target.value })}
            value={site.site_url}
            className="border"
            type="text"
          />
        </div>
        <div className="flex flex-col gap-2 mt-5">
          <label aria-required>{t("country")}</label>
          <select
            onChange={(e) => setSite({ ...site, country_id: e.target.value ? Number(e.target.value) : undefined })}
            value={site.country_id || ""}
            className="border p-2 rounded"
            required
            disabled={isLoadingCountries}
          >
            <option value="">{isLoadingCountries ? t("loading") : t("select_country")}</option>
            {countries.map((country) => (
              <option key={country.id} value={country.id}>
                {i18next.language === "ar" ? country.name_ar : country.name_en}
              </option>
            ))}
          </select>
        </div>
        {/* Logo */}
        <div className="flex flex-col gap-2 mt-5">
          <label aria-required>{t("structure_site_image")}</label>
          <FilePond
            files={logoUrl}
            onupdatefiles={(files) => {
              setLogoUrl(files);
            }}
            allowMultiple={false}
            allowReorder
            allowImagePreview
            allowImageExifOrientation
            maxFiles={1}
            name="files"
            labelIdle='Drag & Drop your files or <span class="filepond--label-action">Browse</span>'
            className={"!mb-0"}
          />
        </div>

        <ButtonsContainer style={{ gap: "2rem" }}>
          <AddButton
            isLoading={isLoading}
            type="submit"
            className="bg-primary !text-text"
          >
            {t("structure_add_site")}
          </AddButton>
          <CancelButton
            type="button"
            style={{ border: "2px solid black" }}
            onClick={() => {
              onClose();
              setResetFileState(!resetFileState);
              setSite({
                asdc_client_sector_id: Number(sectorId),
                site_name: "",
                logo_url: "",
                site_url: "",
                site_serial: "",
                country_id: undefined,
              });
              setLogoUrl([]);
            }}
          >
            {t("cancelBtn")}
          </CancelButton>
        </ButtonsContainer>
      </form>
      <div
        className={`bg-black ${isOpen ? "opacity-60" : "opacity-0 pointer-events-none"
          } absolute inset-0 z-[99999] flex items-center justify-center transition-all duration-500`}
      ></div>
    </>
  );
}
